import { Grid } from '../../../../../Common/Grid'
import { Button, Input, Typography, Divider } from '@fullscreendigital/carrefour-dex-fe'
import { TitleHeader } from '../../../../Common/TitleHeader'
import { getValidationSchema, initialValues } from './helpers'
import { useQuery } from 'react-query'
import {
  makeCoreGetRequest,
  makeCorePostRequest
} from "../../../../../../../utils/api"
import { formatErrorMessage } from '../../../../../../../utils/formatting'
import { useNotification } from '../../../../../../../hooks'
import { useLocation, useNavigate } from 'react-router-dom'
import { Fragment, useMemo, useState } from 'react'
import { useFormik } from 'formik'
import { URLS } from '../../../../constants'
import { CircularProgress } from '@mui/material'
import { ScrollTopButton } from 'components/core-ui/ScrollTopButton'
import { AsyncAutocomplete } from 'components/core-ui/AsyncAutocomplete'

const ReturnStep2 = () => {
  const { showNotification } = useNotification()
  const location = useLocation()
  const navigate = useNavigate()

  const id: string | undefined = location.state?.id
  const supplierCode: string | undefined = location.state?.supplierCode

  if (!id) {
    navigate(URLS.AUTH_SUPPLIERS_RECORD_RETURN)
    return null
  }

  const [packageScanned, setPackageScanned] = useState(() => {
    const saved = localStorage.getItem(`packageScanned-${id}`)
    return saved !== null ? JSON.parse(saved) : false
  })

  const [maxStock, setMaxStock] = useState<number | null>(null)

  const validationSchema = useMemo(() => getValidationSchema(), [])
  const formik = useFormik({
    enableReinitialize: false,
    validateOnChange: false,
    initialValues,
    validationSchema,
    onSubmit: async (values: any) => {
        try {
          const qty = Number(values.supportNumber)
          if (maxStock !== null && !Number.isNaN(qty) && qty > maxStock) {
            showNotification({
              message: 'Cantitatea introdusă este mai mare decât stocul disponibil.',
              type: 'error'
            })
            formik.setFieldError(
              'supportNumber',
              'Cantitatea introdusă este mai mare decât stocul disponibil.'
            )
            return
          }

          await makeCorePostRequest(
            `/shop-reception-mobile/ds-return-truck/ds-return-support-add`,
            { idDSReturnTruck: id, idDSSupportType: values.currentSupportTypeId, supportNumber: values.supportNumber }
          )

          setPackageScanned(true)
          localStorage.setItem(`packageScanned-${id}`, JSON.stringify(true))

          await formik.setFieldValue('supportType', '')
          await formik.setFieldValue('currentSupportTypeId', '')
          await formik.setFieldValue('supportNumber', '')
        } catch (error) {
          showNotification({
            message: `Eroare la trimiterea datelor. ${formatErrorMessage(error)}`,
            type: 'error'
          })
        }
    }
  })

  const { data: returnTruckModel, isLoading: isLoadingReturnTruck } = useQuery(
    'returnTruckModel',
    async () => {
      return await makeCoreGetRequest(`/shop-reception-mobile/ds-return-truck/${id}`, null)
    },
    {
      onError: error => {
        showNotification({
          message: `Eroare la preluarea datelor camionului. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  )

  const { data: supportList, isLoading: isLoadingSupports } = useQuery(
    ['ds-supportList', supplierCode],
    async () => {
      return await makeCoreGetRequest('/shop-reception-mobile/ds-return-truck/ds-support-type-list/supplier', { params: { supplierCode } })
    },
    {
      onError: error => {
        showNotification({
          message: `Eroare la preluarea tipurilor de suport. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  )

  if (isLoadingSupports || isLoadingReturnTruck) {
    return <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />
  }

  const handleSupportTypeChange = async selectedValue => {
    if (selectedValue) {
      const selectedSupport = supportList.find(support => support.id === selectedValue)

      await formik.setFieldValue('supportType', selectedSupport.name)
      await formik.setFieldValue('currentSupportTypeId', selectedSupport.id)

      try {
        const shopCodeFilter = `[${returnTruckModel.shopCode}]`
        const supplierCodeFilter = returnTruckModel.supplierCode
        const supportNameFilter = selectedSupport.name

        const response = await makeCoreGetRequest(
          '/shop-reception/ds-support-stock',
          {
            params: {
              page: 0,
              size: 1,
              order: 'desc',
              orderBy: 'lastUpdate',
              filterBy: 'shopCode|supplierCode|supportName',
              filterValue: `${shopCodeFilter}|${supplierCodeFilter}|${supportNameFilter}`
            }
          }
        )

        const stock = response?.list?.[0]?.stock

        if (typeof stock === 'number') {
          setMaxStock(stock)
          await formik.setFieldValue('supportNumber', stock)
          formik.setFieldError('supportNumber', undefined)
        } else {
          setMaxStock(null)
          await formik.setFieldValue('supportNumber', '')
        }
      } catch (error) {
        showNotification({
          message: `Eroare la preluarea stocului. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    } else {
      setMaxStock(null)
      await formik.setFieldValue('supportNumber', '')
      formik.setFieldError('supportNumber', undefined)
    }
  }

  const hasSupports = !!supportList && supportList.length > 0

  let items = [
    <TitleHeader title="ÎNREGISTREAZǍ CAMION" />,
    <>
      <Typography variant="medium" component="p">
        Numǎr camion: {returnTruckModel?.truckNumber}
      </Typography>
      <Typography variant="medium" component="p">
        Furnizor: {returnTruckModel?.supplierName}
      </Typography>
    </>,
    hasSupports ? (
      <AsyncAutocomplete
        variant="outlined"
        name="searchSupportType"
        label="Cauta tip suport"
        options={supportList.map(support => ({
          value: support.id,
          label: support.code
        }))}
        onChange={support => handleSupportTypeChange(support?.value)}
        error={!!formik.errors.supportType}
        helperText={formik.errors.supportType as string}
      />
    ) : (
      <Input
        type="text"
        label="Cauta tip suport"
        variant="outlined"
        width="100%"
        readonly
        value=""
        placeholder="Nu există tipuri de suport pentru acest furnizor."
        style={{ marginBottom: '15px' }}
      />
    ),
    <Divider />,
    formik.values.currentSupportTypeId && (
      <Fragment>
        <Input
          type="text"
          label="Tip ambalaj"
          variant="outlined"
          width="100%"
          value={formik.values.supportType}
          style={{ marginBottom: '15px' }}
          readonly
        />
        <Input
          type="number"
          label="Număr ambalaje"
          variant="outlined"
          width="100%"
          value={formik.values.supportNumber}
          onChange={event => {
            const rawValue = (event.target as HTMLInputElement).value

            if (rawValue === '') {
              formik.setFieldValue('supportNumber', '')
              formik.setFieldError('supportNumber', undefined)
              return
            }

            const numericValue = Number(rawValue)

            if (maxStock !== null && numericValue > maxStock) {
              formik.setFieldError(
                'supportNumber',
                'Cantitatea introdusă este mai mare decât stocul disponibil.'
              )
            } else {
              formik.setFieldError('supportNumber', undefined)
            }

            formik.setFieldValue('supportNumber', numericValue)
          }}
          error={!!formik.errors.supportNumber}
          helperText={formik.errors.supportNumber as string}
        />
        <Button
          type="button"
          variant="contained"
          label="PASUL URMǍTOR"
          onClick={() => formik.handleSubmit()}
          disabled={formik.isSubmitting}
          style={{ width: '100%', marginTop: '15px' }}
        />
      </Fragment>
    ),
    !formik.values.currentSupportTypeId && (
      <Button
        type="button"
        variant="contained"
        label="PASUL URMǍTOR - SUMAR"
        onClick={() => navigate(URLS.AUTH_SUPPLIERS_RETURN_STEP_3, { state: { id, supplierCode } })}
        disabled={!packageScanned}
        style={{ width: '100%' }}
      />
    ),
    <Button
      type="button"
      variant="contained"
      label="Modifica Date Camion"
      onClick={() => navigate(URLS.AUTH_SUPPLIERS_RECORD_RETURN, { state: { truckId: id } })}
      style={{ width: '100%', marginTop: '15px' }}
    />,
    <ScrollTopButton />
  ]

  return (
    <Grid gridContainerStyle={{ marginTop: '20px' }} maxWidth="xs" items={items} itemsPerRow={1} />
  )
}

export default ReturnStep2
